import { useOffres, type OffrePresentee } from '../../api/offres.js';
import { aTraiter, type ContexteAction, type SegmentOffres } from './syntheseOffre.js';

/** Au-delà, la liste serait tronquée : la page le dit au lieu de l'ignorer. */
const PAR_PAGE = 200;

export interface VueOffres {
  chargement: boolean;
  erreur: boolean;
  /** Brouillons à envoyer, envoyées à relancer ou échues, acceptées à finaliser. */
  aTraiter: {
    brouillons: OffrePresentee[];
    relances: OffrePresentee[];
    acceptees: OffrePresentee[];
  };
  negociation: OffrePresentee[];
  acceptees: OffrePresentee[];
  closes: OffrePresentee[];
  toutes: OffrePresentee[];
  compteurs: Record<Exclude<SegmentOffres, 'toutes'>, number>;
  /** Pages du segment affiché ; seuls « closes » et « toutes » en ont plusieurs. */
  pages: number;
}

/**
 * Les offres regroupées par ce qu'elles attendent. L'API ne sait filtrer que par
 * état : les segments « à traiter » et « en négociation » sont composés ici à
 * partir des listes par état, sans nouveau calcul serveur.
 */
export function useVueOffres(
  galerieSlug: string,
  segment: SegmentOffres,
  page: number,
  contexte: ContexteAction,
): VueOffres {
  const brouillons = useOffres(galerieSlug, { etat: 'brouillon', parPage: PAR_PAGE });
  const envoyees = useOffres(galerieSlug, { etat: 'envoyee', parPage: PAR_PAGE });
  const acceptees = useOffres(galerieSlug, { etat: 'acceptee', parPage: PAR_PAGE });
  const closes = useOffres(galerieSlug, {
    closes: true,
    parPage: PAR_PAGE,
    page: segment === 'closes' ? page : 1,
  });
  const toutes = useOffres(galerieSlug, { page }, { enabled: segment === 'toutes' });
  const requetes = [
    brouillons,
    envoyees,
    acceptees,
    closes,
    ...(segment === 'toutes' ? [toutes] : []),
  ];
  const maintenant = new Date();
  const listeBrouillons = brouillons.data?.offres ?? [];
  const listeEnvoyees = envoyees.data?.offres ?? [];
  const listeAcceptees = acceptees.data?.offres ?? [];
  const relances = listeEnvoyees.filter((o) => aTraiter(o, contexte, maintenant));
  const aFinaliser = listeAcceptees.filter((o) => aTraiter(o, contexte, maintenant));
  return {
    chargement: requetes.some((r) => r.isLoading),
    erreur: requetes.some((r) => r.isError),
    aTraiter: { brouillons: listeBrouillons, relances, acceptees: aFinaliser },
    negociation: listeEnvoyees,
    acceptees: listeAcceptees,
    closes: closes.data?.offres ?? [],
    toutes: toutes.data?.offres ?? [],
    compteurs: {
      aTraiter: listeBrouillons.length + relances.length + aFinaliser.length,
      negociation: envoyees.data?.pagination.total ?? 0,
      acceptees: acceptees.data?.pagination.total ?? 0,
      closes: closes.data?.pagination.total ?? 0,
    },
    pages:
      segment === 'closes'
        ? (closes.data?.pagination.pages ?? 1)
        : segment === 'toutes'
          ? (toutes.data?.pagination.pages ?? 1)
          : 1,
  };
}
